import React, { useState, useEffect, useRef } from 'react';
import {
  Target,
  TrendingUp,
  FileText,
  CheckSquare,
  Award,
  BarChart3,
  BookOpen,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  CheckCircle,
  GripVertical,
  Save,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Zap,
  AlertCircle as AlertCircleIcon
} from 'lucide-react';
import { supabase } from '../../../../../lib/supabase';
import toast from 'react-hot-toast';
import DeadlineIntelligenceService from '../../../../../services/DeadlineIntelligenceService';

const PortfolioStrategy = ({ colleges = [], currentUser, portfolioStatus = {}, studentProfile = {}, currentView = 'portfolio', onViewChange }) => {
  // Debug logging for props
  useEffect(() => {
    console.log('📋 [PortfolioStrategy] Component mounted/updated with props:', {
      studentProfileKeys: studentProfile ? Object.keys(studentProfile) : 'null/undefined',
      studentProfileIsEmpty: !studentProfile || Object.keys(studentProfile).length === 0,
      studentProfileSample: studentProfile ? {
        sat_total: studentProfile.sat_total,
        gpa_weighted: studentProfile.gpa_weighted,
        act_composite: studentProfile.act_composite,
        intended_major: studentProfile.intended_major
      } : 'empty',
      currentUserId: currentUser?.id
    });
  }, [studentProfile, currentUser]);

  const [activeTab, setActiveTab] = useState('ed-strategy');
  const tabContentRef = useRef(null);

  // Handle tab change with scroll to tab content
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Scroll to the tab content section with offset for sticky nav
    if (tabContentRef.current) {
      const element = tabContentRef.current;
      const offsetTop = element.offsetTop - 200; // Account for sticky nav height + some padding
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };



  // AI Analysis State
  const [edStrategy, setEDStrategy] = useState(null);
  const [portfolioConfidence, setPortfolioConfidence] = useState(null);
  const [applicationOrder, setApplicationOrder] = useState(null);
  const [essayStrategy, setEssayStrategy] = useState(null);
  const [decisionFramework, setDecisionFramework] = useState(null);

  // Loading States
  const [loadingED, setLoadingED] = useState(false);
  const [loadingConfidence, setLoadingConfidence] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingEssay, setLoadingEssay] = useState(false);
  const [loadingFramework, setLoadingFramework] = useState(false);
  const [_loadingCache, setLoadingCache] = useState(true);

  // Priority Order Management
  const [customPriorityOrder, setCustomPriorityOrder] = useState([]);
  const [savingPriority, setSavingPriority] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hours breakdown expansion
  const [expandedColleges, setExpandedColleges] = useState(new Set());
  const [savingMVV, setSavingMVV] = useState(false);

  // Find ED school if selected
  const edSchool = colleges.find(c => c.application_round === 'early_decision');

  // Initialize priority order from colleges data
  useEffect(() => {
    if (colleges.length > 0) {
      // Sort colleges by existing priority_order or calculate recommended order
      const collegesWithDeadlines = colleges.map(college => {
        const gc = college.global_colleges;
        const applicationRound = college.application_round;

        return {
          id: college.id,
          name: gc?.name || college.name,
          tier: college.category,
          application_round: applicationRound,
          priority_order: college.priority_order,
          deadlines: {
            ed: gc?.ed_deadline,
            ed2: gc?.ed2_deadline,
            ea: gc?.ea_deadline,
            rd: gc?.rd_deadline
          }
        };
      });

      // Check if any colleges have priority_order set
      const hasCustomOrder = collegesWithDeadlines.some(c => c.priority_order !== null && c.priority_order !== undefined);

      let orderedColleges;
      if (hasCustomOrder) {
        // Use existing priority_order
        orderedColleges = [...collegesWithDeadlines].sort((a, b) => {
          const aPriority = a.priority_order ?? 999;
          const bPriority = b.priority_order ?? 999;
          return aPriority - bPriority;
        });
      } else {
        // Calculate recommended order using deadline intelligence
        orderedColleges = [...collegesWithDeadlines].sort((a, b) => {
          // ED schools always come first
          const aIsED = a.application_round?.includes('early_decision');
          const bIsED = b.application_round?.includes('early_decision');

          if (aIsED && !bIsED) return -1;
          if (!aIsED && bIsED) return 1;

          // If both ED or both not ED, sort by deadline
          // Get the relevant deadline for each college
          const getRelevantDeadline = (college) => {
            const round = college.application_round;
            switch(round) {
              case 'early_decision':
              case 'early_decision_1':
                return college.deadlines?.ed;
              case 'early_decision_2':
                return college.deadlines?.ed2;
              case 'early_action':
                return college.deadlines?.ea;
              case 'regular_decision':
              default:
                return college.deadlines?.rd;
            }
          };

          const aDeadline = getRelevantDeadline(a);
          const bDeadline = getRelevantDeadline(b);

          if (aDeadline && bDeadline) {
            return new Date(aDeadline) - new Date(bDeadline);
          }
          if (aDeadline) return -1;
          if (bDeadline) return 1;
          return 0;
        });
      }

      setCustomPriorityOrder(orderedColleges);
    }
  }, [colleges]);

  // Load cached strategy on mount
  useEffect(() => {
    const fetchCachedStrategy = async () => {
      if (!currentUser?.id) {
        setLoadingCache(false);
        return;
      }

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser.id;

      console.log('📥 [PortfolioStrategy] Starting cache fetch...');
      console.log('   userIdForQuery:', userIdForQuery);
      console.log('   currentUser:', currentUser);

      try {
        // First check auth state
        const { data: authData, error: authError } = await supabase.auth.getUser();
        console.log('🔐 Auth check result:', {
          hasAuthData: !!authData,
          hasUser: !!authData?.user,
          authUserId: authData?.user?.id,
          authError: authError?.message
        });

        if (!authData?.user) {
          console.error('❌ No authenticated user found! This will cause RLS to block.');
        }
      } catch (authCheckError) {
        console.error('❌ Error checking auth:', authCheckError);
      }

      try {
        console.log('🔍 [PortfolioStrategy] Executing query:', {
          table: 'portfolio_analysis',
          filter: `user_id = ${userIdForQuery}`,
          method: 'single()'
        });

        const { data, error } = await supabase
          .from('portfolio_analysis')
          .select('ed_strategy, portfolio_confidence, application_order, essay_strategy, decision_framework')
          .eq('user_id', userIdForQuery)
          .single();

        console.log('📦 [PortfolioStrategy] Query result:', {
          hasData: !!data,
          hasError: !!error,
          errorCode: error?.code,
          errorMessage: error?.message
        });

        if (error) {
          if (error.code === 'PGRST116') {
            console.log('📭 [PortfolioStrategy] No cached data found for user:', userIdForQuery);
            console.log('   This means either:');
            console.log('   1. No data exists in database for this user_id');
            console.log('   2. RLS is blocking the query (auth.uid() mismatch)');
          } else {
            console.error('❌ [PortfolioStrategy] Error fetching cached strategy:', error);
            console.error('   Error code:', error.code);
            console.error('   Error message:', error.message);
            console.error('   Error details:', error.details);
            console.error('   Error hint:', error.hint);
          }
          setLoadingCache(false);
          return;
        }

        if (data) {
          console.log('✅ [PortfolioStrategy] Loaded cached data:', {
            has_ed_strategy: !!data.ed_strategy,
            has_portfolio_confidence: !!data.portfolio_confidence,
            has_application_order: !!data.application_order,
            has_essay_strategy: !!data.essay_strategy,
            has_decision_framework: !!data.decision_framework
          });
          if (data.ed_strategy) setEDStrategy(data.ed_strategy);
          if (data.portfolio_confidence) setPortfolioConfidence(data.portfolio_confidence);
          if (data.application_order) setApplicationOrder(data.application_order);
          if (data.essay_strategy) setEssayStrategy(data.essay_strategy);
          if (data.decision_framework) setDecisionFramework(data.decision_framework);
        }
      } catch (error) {
        console.error('❌ [PortfolioStrategy] Unexpected error loading cached strategy:', error);
      } finally {
        setLoadingCache(false);
      }
    };

    fetchCachedStrategy();
  }, [currentUser?.id]);

  // Analyze ED Strategy
  const analyzeEDStrategy = async () => {
    setLoadingED(true);

    // Check for selected student ID (for parent users)
    const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
    const userIdForQuery = selectedStudentUserId || currentUser?.id;

    console.log('🚀 [ED Strategy] Preparing API call with:', {
      userIdForQuery,
      studentProfileKeys: studentProfile ? Object.keys(studentProfile) : 'null/undefined',
      studentProfileSample: studentProfile ? {
        sat_total: studentProfile.sat_total,
        gpa_weighted: studentProfile.gpa_weighted,
        intended_major: studentProfile.intended_major
      } : 'empty',
      profileIsEmpty: !studentProfile || Object.keys(studentProfile).length === 0
    });

    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'analyze-ed-strategy',
          edCollege: edSchool ? {
            name: edSchool.global_colleges?.name || edSchool.name,
            acceptance_rate: edSchool.global_colleges?.acceptance_rate,
            ed_acceptance_rate: edSchool.global_colleges?.ed_acceptance_rate,
            tier: edSchool.category,
            location: `${edSchool.global_colleges?.city}, ${edSchool.global_colleges?.state}`,
            ranking: edSchool.global_colleges?.us_news_ranking,
            ...edSchool.global_colleges
          } : null,
          portfolio: colleges.map(c => ({
            name: c.global_colleges?.name || c.name,
            tier: c.category,
            acceptance_rate: c.global_colleges?.acceptance_rate,
            application_round: c.application_round,
            ...c.global_colleges
          })),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze ED strategy');
      }

      const result = await response.json();
      console.log('✅ [ED Strategy] API response:', result);

      setEDStrategy(result.analysis);
      toast.success('ED strategy analysis complete');
    } catch (error) {
      console.error('❌ [ED Strategy] Error:', error);
      toast.error(error.message || 'Failed to analyze ED strategy');
    } finally {
      setLoadingED(false);
    }
  };

  // Calculate Portfolio Confidence
  const calculateConfidence = async () => {
    setLoadingConfidence(true);

    // Check for selected student ID (for parent users)
    const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
    const userIdForQuery = selectedStudentUserId || currentUser?.id;

    console.log('🚀 [Portfolio Confidence] Preparing API call with:', {
      userIdForQuery,
      studentProfileKeys: studentProfile ? Object.keys(studentProfile) : 'null/undefined',
      studentProfileSample: studentProfile ? {
        sat_total: studentProfile.sat_total,
        gpa_weighted: studentProfile.gpa_weighted,
        intended_major: studentProfile.intended_major
      } : 'empty',
      profileIsEmpty: !studentProfile || Object.keys(studentProfile).length === 0
    });

    try {
      if (colleges.length < 4) {
        toast.error('Add at least 4 colleges for confidence calculation');
        return;
      }

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'calculate-portfolio-confidence',
          portfolio: colleges.map(c => ({
            name: c.global_colleges?.name || c.name,
            tier: c.category,
            acceptance_rate: c.global_colleges?.acceptance_rate,
            ed_acceptance_rate: c.global_colleges?.ed_acceptance_rate,
            location: `${c.global_colleges?.city}, ${c.global_colleges?.state}`,
            ...c.global_colleges
          })),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate confidence');
      }

      const { analysis } = await response.json();
      setPortfolioConfidence(analysis);
      toast.success('Portfolio confidence calculated');
    } catch (error) {
      console.error('Error calculating confidence:', error);
      toast.error(error.message || 'Failed to calculate confidence');
    } finally {
      setLoadingConfidence(false);
    }
  };

  // Generate Application Order
  const generateApplicationOrder = async () => {
    setLoadingOrder(true);

    // Check for selected student ID (for parent users)
    const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
    const userIdForQuery = selectedStudentUserId || currentUser?.id;

    console.log('🚀 [Application Order] Saving for user:', userIdForQuery);

    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-application-order',
          portfolio: colleges.map(c => ({
            name: c.global_colleges?.name || c.name,
            tier: c.category,
            application_round: c.application_round,
            deadline: c.deadline_date,
            ...c.global_colleges
          })),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate application order');
      }

      const { analysis } = await response.json();
      setApplicationOrder(analysis);
      toast.success('Application order generated');
    } catch (error) {
      console.error('Error generating application order:', error);
      toast.error(error.message || 'Failed to generate application order');
    } finally {
      setLoadingOrder(false);
    }
  };

  // Create Essay Strategy
  const createEssayStrategy = async () => {
    setLoadingEssay(true);

    // Check for selected student ID (for parent users)
    const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
    const userIdForQuery = selectedStudentUserId || currentUser?.id;

    console.log('🚀 [Essay Strategy] Saving for user:', userIdForQuery);

    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-essay-strategy',
          portfolio: colleges.map(c => ({
            name: c.global_colleges?.name || c.name,
            ...c.global_colleges
          })),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create essay strategy');
      }

      const { analysis } = await response.json();
      setEssayStrategy(analysis);
      toast.success('Essay strategy created');
    } catch (error) {
      console.error('Error creating essay strategy:', error);
      toast.error(error.message || 'Failed to create essay strategy');
    } finally {
      setLoadingEssay(false);
    }
  };

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = React.useState(null);

  // Toggle college expansion for hours breakdown
  const toggleCollegeExpansion = (collegeId) => {
    setExpandedColleges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collegeId)) {
        newSet.delete(collegeId);
      } else {
        newSet.add(collegeId);
      }
      return newSet;
    });
  };

  // Calculate hours for a specific college
  const calculateCollegeHours = async (collegeId) => {
    try {
      // Fetch timeline items for this college
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      const { data: tasks, error } = await supabase
        .from('timeline_items')
        .select('id, title, estimated_duration, mvv_time_required, mvv_selected, completion_status')
        .eq('user_id', userIdForQuery)
        .eq('college_id', collegeId)
        .neq('completion_status', 'completed');

      if (error) throw error;

      // Calculate total hours (standard and MVV)
      const standardHours = tasks.reduce((sum, task) => {
        const hours = (task.estimated_duration || 0) / 60; // Convert minutes to hours
        return sum + hours;
      }, 0);

      const mvvHours = tasks.reduce((sum, task) => {
        const hours = (task.mvv_time_required || task.estimated_duration || 0) / 60;
        return sum + hours;
      }, 0);

      const currentHours = tasks.reduce((sum, task) => {
        const hours = task.mvv_selected
          ? (task.mvv_time_required || 0) / 60
          : (task.estimated_duration || 0) / 60;
        return sum + hours;
      }, 0);

      return {
        tasks,
        standardHours: Math.round(standardHours * 10) / 10,
        mvvHours: Math.round(mvvHours * 10) / 10,
        currentHours: Math.round(currentHours * 10) / 10,
        timeSaved: Math.round((standardHours - mvvHours) * 10) / 10
      };
    } catch (error) {
      console.error('Error calculating college hours:', error);
      return { tasks: [], standardHours: 0, mvvHours: 0, currentHours: 0, timeSaved: 0 };
    }
  };

  // Toggle MVV mode for an entire college
  const toggleCollegeMVV = async (collegeId, enableMVV) => {
    setSavingMVV(true);
    try {
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      // Update all incomplete tasks for this college
      const { error } = await supabase
        .from('timeline_items')
        .update({ mvv_selected: enableMVV })
        .eq('user_id', userIdForQuery)
        .eq('college_id', collegeId)
        .neq('completion_status', 'completed');

      if (error) throw error;

      toast.success(enableMVV
        ? 'Switched to Minimum Viable Version mode'
        : 'Switched to Standard mode'
      );

      // Force re-render to update hours
      setExpandedColleges(new Set(expandedColleges));
    } catch (error) {
      console.error('Error toggling MVV mode:', error);
      toast.error('Failed to update MVV mode');
    } finally {
      setSavingMVV(false);
    }
  };

  // Move college up in priority order
  const moveCollegeUp = (index) => {
    if (index === 0) return; // Already at top
    const newOrder = [...customPriorityOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setCustomPriorityOrder(newOrder);
    setHasUnsavedChanges(true);
  };

  // Move college down in priority order
  const moveCollegeDown = (index) => {
    if (index === customPriorityOrder.length - 1) return; // Already at bottom
    const newOrder = [...customPriorityOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setCustomPriorityOrder(newOrder);
    setHasUnsavedChanges(true);
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...customPriorityOrder];
    const draggedItem = newOrder[draggedIndex];

    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    // Insert at new position
    newOrder.splice(index, 0, draggedItem);

    setCustomPriorityOrder(newOrder);
    setDraggedIndex(index);
    setHasUnsavedChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save priority order to database
  const savePriorityOrder = async () => {
    setSavingPriority(true);

    try {
      // Update each college's priority_order
      const updates = customPriorityOrder.map((college, index) =>
        supabase
          .from('college_journey_colleges')
          .update({ priority_order: index + 1 })
          .eq('id', college.id)
      );

      await Promise.all(updates);

      toast.success('Application order saved successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving priority order:', error);
      toast.error('Failed to save application order');
    } finally {
      setSavingPriority(false);
    }
  };

  // Build Decision Framework
  const buildDecisionFramework = async () => {
    setLoadingFramework(true);

    // Check for selected student ID (for parent users)
    const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
    const userIdForQuery = selectedStudentUserId || currentUser?.id;

    console.log('🚀 [Decision Framework] Saving for user:', userIdForQuery);

    try {
      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'build-decision-framework',
          portfolio: colleges.map(c => ({
            name: c.global_colleges?.name || c.name,
            tier: c.category,
            ...c.global_colleges
          })),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to build decision framework');
      }

      const { analysis } = await response.json();
      setDecisionFramework(analysis);
      toast.success('Decision framework created');
    } catch (error) {
      console.error('Error building decision framework:', error);
      toast.error(error.message || 'Failed to build decision framework');
    } finally {
      setLoadingFramework(false);
    }
  };

  // Tab definitions
  const tabs = [
    { id: 'ed-strategy', label: 'Early Decision Strategy', icon: Target },
    { id: 'portfolio-confidence', label: 'Portfolio Confidence', icon: BarChart3 },
    { id: 'recommendations', label: 'Strategic Recommendations', icon: TrendingUp },
    { id: 'essay-strategy', label: 'Essay & Narrative', icon: FileText },
    { id: 'decision-framework', label: 'Decision Framework', icon: CheckSquare }
  ];

  const renderEDStrategy = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-slate-900">
          Early Decision Strategy {edSchool && `- ${edSchool.global_colleges?.name || edSchool.name}`}
        </h3>
        <button
          onClick={analyzeEDStrategy}
          disabled={loadingED}
          className="px-4 py-2 bg-[#643eef] hover:bg-[#5534d1] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        >
          {loadingED ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Analyze ED Strategy
            </>
          )}
        </button>
      </div>

      {/* AI Analysis Results */}
      {edStrategy && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          <div className="flex items-start gap-2 mb-4">
            <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              AI Strategy Analysis
            </h4>
          </div>

          {edStrategy.shouldApplyED !== undefined && (
            <div className={`rounded-lg border-2 px-4 py-3 mb-6 flex items-center justify-between ${
              edStrategy.shouldApplyED === 'Yes'
                ? 'bg-green-50 border-[#26a69a]'
                : 'bg-red-50 border-[#ff6b6b]'
            }`}>
              <span className={`text-sm font-semibold ${
                edStrategy.shouldApplyED === 'Yes' ? 'text-green-800' : 'text-red-700'
              }`}>
                Recommendation: {edStrategy.shouldApplyED}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {edStrategy.edConfidence && (
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">ED Confidence</p>
                <span className="text-2xl font-bold text-[#643eef] leading-none">{edStrategy.edConfidence}/10</span>
              </div>
            )}
            {edStrategy.admissionBoost && (
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">Admission Boost</p>
                <span className="text-lg font-bold text-stone-700">{edStrategy.admissionBoost}</span>
              </div>
            )}
            {edStrategy.financialRisk && (
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">Financial Risk</p>
                <span className="text-lg font-bold text-[#ff6b6b]">{edStrategy.financialRisk}</span>
              </div>
            )}
          </div>

          {edStrategy.reasoning && (
            <div className="mb-6">
              <p className="text-sm text-stone-700 leading-relaxed italic">{edStrategy.reasoning}</p>
            </div>
          )}

          <div className="h-px bg-stone-200 my-6"></div>

          <div className="space-y-4">
            {edStrategy.pros && edStrategy.pros.length > 0 && (
              <div>
                <h5 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                  Pros of Applying ED
                </h5>
                <ul className="space-y-2">
                  {edStrategy.pros.map((pro, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                      <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {edStrategy.cons && edStrategy.cons.length > 0 && (
              <div>
                <h5 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                  Cons/Risks
                </h5>
                <ul className="space-y-2">
                  {edStrategy.cons.map((con, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                      <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {edStrategy.alternativeStrategy && (
              <>
                <div className="h-px bg-stone-200 my-6"></div>
                <div>
                  <h5 className="font-semibold text-slate-900 text-base mb-2">Alternative Strategy</h5>
                  <p className="text-sm text-stone-700 leading-relaxed">{edStrategy.alternativeStrategy}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Default Info */}
      {!edStrategy && !loadingED && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          {edSchool ? (
            <>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">
                ED School Selected: {edSchool.global_colleges?.name || edSchool.name}
              </h4>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Click "Analyze ED Strategy" to get personalized AI analysis of your Early Decision choice,
                including confidence scores, pros/cons, and alternative strategies.
              </p>
            </>
          ) : (
            <>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">Should You Apply ED?</h4>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Early Decision can significantly boost admission chances but requires commitment.
                Consider these factors:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  You have a clear top choice school
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Financial aid is not a primary concern OR the school meets full need
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Your application is ready by November 1
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  You're prepared to commit if admitted
                </li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderPortfolioConfidence = () => {
    const totalColleges = colleges.length;
    const reachCount = colleges.filter(c => c.category?.toLowerCase() === 'reach').length;
    const targetCount = colleges.filter(c => c.category?.toLowerCase() === 'target').length;
    const safetyCount = colleges.filter(c => c.category?.toLowerCase() === 'safety').length;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-900">Portfolio Confidence Analysis</h3>
          <button
            onClick={calculateConfidence}
            disabled={loadingConfidence || colleges.length < 4}
            className="px-4 py-2 bg-[#4290f5] hover:bg-[#2e7ae0] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
          >
            {loadingConfidence ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Calculate Confidence
              </>
            )}
          </button>
        </div>

        {/* AI Analysis Results */}
        {portfolioConfidence && (
          <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
            <div className="flex items-start gap-2 mb-6">
              <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                AI Confidence Analysis
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 pb-6 border-b border-stone-200">
              {portfolioConfidence.overallStrength && (
                <div>
                  <p className="text-xs font-semibold text-stone-600 mb-1">Overall Strength</p>
                  <div className="text-3xl font-bold text-[#4290f5] leading-none mb-1">{portfolioConfidence.overallStrength}/10</div>
                </div>
              )}
              {portfolioConfidence.tierBalance && (
                <div>
                  <p className="text-xs font-semibold text-stone-600 mb-1">Tier Balance</p>
                  <div className="text-sm text-stone-700 space-y-0.5">
                    <p>Reach: <strong>{portfolioConfidence.tierBalance.reach || reachCount}</strong></p>
                    <p>Target: <strong>{portfolioConfidence.tierBalance.target || targetCount}</strong></p>
                    <p>Safety: <strong>{portfolioConfidence.tierBalance.safety || safetyCount}</strong></p>
                  </div>
                </div>
              )}
              {portfolioConfidence.admissionProbability && (
                <div>
                  <p className="text-xs font-semibold text-stone-600 mb-1">Admission Likelihood</p>
                  <div className="text-sm space-y-0.5">
                    <p className="text-[#26a69a] font-semibold">
                      ≥1 school: {portfolioConfidence.admissionProbability.atLeastOne || 'High'}
                    </p>
                    <p className="text-[#f59e0b] font-semibold">
                      Multiple: {portfolioConfidence.admissionProbability.multiple || 'Medium'}
                    </p>
                  </div>
                </div>
              )}
              {portfolioConfidence.riskAssessment && (
                <div>
                  <p className="text-xs font-semibold text-stone-600 mb-1">Risk Level</p>
                  <span className={`inline-block px-3 py-1 rounded-2xl text-xs font-semibold mt-1 ${
                    portfolioConfidence.riskAssessment === 'Low' ? 'bg-green-100 text-green-800' :
                    portfolioConfidence.riskAssessment === 'Medium' ? 'bg-amber-100 text-amber-900' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {portfolioConfidence.riskAssessment}
                  </span>
                </div>
              )}
            </div>

            {portfolioConfidence.explanation && (
              <div className="mb-6">
                <p className="text-sm text-stone-700 leading-relaxed">{portfolioConfidence.explanation}</p>
              </div>
            )}

            {portfolioConfidence.recommendations && portfolioConfidence.recommendations.length > 0 && (
              <>
                <div className="h-px bg-stone-200 my-6"></div>
                <div>
                  <h5 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                    Recommendations
                  </h5>
                  <ul className="space-y-2">
                    {portfolioConfidence.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                        <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        )}

        {/* Basic Stats Display */}
        {!portfolioConfidence && !loadingConfidence && (
          <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
            <h4 className="font-semibold text-slate-900 mb-4 text-sm">Current Portfolio</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">Total Schools</p>
                <span className="text-2xl font-bold text-slate-900 leading-none">{totalColleges}</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">Tier Distribution</p>
                <div className="text-sm text-stone-700 space-y-0.5">
                  <p>Reach: <strong>{reachCount}</strong></p>
                  <p>Target: <strong>{targetCount}</strong></p>
                  <p>Safety: <strong>{safetyCount}</strong></p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-600 mb-1">Status</p>
                <span className="text-sm text-stone-700">
                  {colleges.length >= 4 ? 'Ready for analysis' : `Add ${4 - colleges.length} more schools`}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // College Hours Breakdown Component
  const CollegeHoursBreakdown = ({ collegeId, collegeName, calculateCollegeHours, toggleCollegeMVV, savingMVV }) => {
    const [hoursData, setHoursData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadHours = async () => {
        setLoading(true);
        const data = await calculateCollegeHours(collegeId);
        setHoursData(data);
        setLoading(false);
      };
      loadHours();
    }, [collegeId, savingMVV]); // Re-fetch when MVV mode changes

    if (loading) {
      return (
        <div className="px-4 pb-4 bg-stone-50">
          <div className="animate-pulse flex items-center gap-2 text-stone-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading hours breakdown...</span>
          </div>
        </div>
      );
    }

    if (!hoursData) return null;

    const { standardHours, mvvHours, currentHours, timeSaved, tasks } = hoursData;
    const isMVVMode = tasks.some(t => t.mvv_selected);

    return (
      <div className="px-4 pb-4 bg-stone-50 border-t border-stone-200">
        <div className="pt-4 space-y-4">
          {/* Hours Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-stone-600 uppercase">Standard</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">{standardHours}h</div>
              <p className="text-xs text-stone-600 mt-0.5">Full quality application</p>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-semibold text-stone-600 uppercase">MVV Mode</span>
              </div>
              <div className="text-2xl font-bold text-orange-600">{mvvHours}h</div>
              <p className="text-xs text-stone-600 mt-0.5">Minimum viable version</p>
            </div>

            <div className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold text-stone-600 uppercase">Current</span>
              </div>
              <div className="text-2xl font-bold text-green-600">{currentHours}h</div>
              <p className="text-xs text-stone-600 mt-0.5">With current settings</p>
            </div>
          </div>

          {/* MVV Toggle Section */}
          <div className="bg-white border-2 border-stone-300 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-orange-600" />
                  <h4 className="font-semibold text-slate-900">Minimum Viable Version Mode</h4>
                </div>
                <p className="text-sm text-stone-700 leading-relaxed mb-2">
                  MVV mode reduces time requirements by focusing on essential tasks only.
                  Saves <strong>{timeSaved} hours</strong> but may reduce application quality.
                </p>
                {isMVVMode && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
                    <div className="flex items-start gap-2">
                      <AlertCircleIcon className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-900 mb-1">Risk Awareness</p>
                        <p className="text-xs text-amber-800 leading-relaxed">
                          Using MVV mode may result in a less competitive application.
                          Consider this carefully for reach and target schools.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => toggleCollegeMVV(collegeId, true)}
                  disabled={savingMVV || isMVVMode}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    isMVVMode
                      ? 'bg-orange-600 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-orange-100 hover:text-orange-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isMVVMode && '✓ '}MVV Mode
                </button>
                <button
                  onClick={() => toggleCollegeMVV(collegeId, false)}
                  disabled={savingMVV || !isMVVMode}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    !isMVVMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-blue-100 hover:text-blue-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {!isMVVMode && '✓ '}Standard
                </button>
              </div>
            </div>
          </div>

          {/* Task Breakdown */}
          <details className="bg-white border border-stone-200 rounded-lg">
            <summary className="px-4 py-3 cursor-pointer hover:bg-stone-50 font-medium text-sm text-slate-900 flex items-center justify-between">
              <span>View {tasks.length} task breakdown</span>
              <ChevronDown className="w-4 h-4 text-stone-500" />
            </summary>
            <div className="px-4 pb-4 pt-2 border-t border-stone-200">
              <div className="space-y-2">
                {tasks.map(task => {
                  const standardTime = (task.estimated_duration || 0) / 60;
                  const mvvTime = (task.mvv_time_required || task.estimated_duration || 0) / 60;
                  const currentTime = task.mvv_selected ? mvvTime : standardTime;

                  return (
                    <div key={task.id} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-stone-700 flex-1">{task.title}</span>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-stone-500 w-16 text-right">
                          {standardTime.toFixed(1)}h std
                        </span>
                        {mvvTime !== standardTime && (
                          <span className="text-orange-600 w-16 text-right">
                            {mvvTime.toFixed(1)}h mvv
                          </span>
                        )}
                        <span className={`font-semibold w-16 text-right ${
                          task.mvv_selected ? 'text-orange-600' : 'text-blue-600'
                        }`}>
                          {currentTime.toFixed(1)}h
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        </div>
      </div>
    );
  };

  // Helper to get deadline text for college
  const getDeadlineText = (college) => {
    const round = college.application_round;
    let deadline = null;

    switch(round) {
      case 'early_decision':
      case 'early_decision_1':
        deadline = college.deadlines?.ed;
        break;
      case 'early_decision_2':
        deadline = college.deadlines?.ed2;
        break;
      case 'early_action':
        deadline = college.deadlines?.ea;
        break;
      case 'regular_decision':
      default:
        deadline = college.deadlines?.rd;
        break;
    }

    if (!deadline) return 'No deadline set';

    const date = new Date(deadline);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Helper to get round display text
  const getRoundDisplay = (round) => {
    const roundMap = {
      'early_decision': 'ED',
      'early_decision_1': 'ED1',
      'early_decision_2': 'ED2',
      'early_action': 'EA',
      'regular_decision': 'RD'
    };
    return roundMap[round] || round;
  };

  const renderRecommendations = () => (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-slate-900">Application Order Strategy</h3>
          <p className="text-sm text-stone-600 mt-1">
            Organize your colleges in the order you'll apply to them
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-700 font-medium">Unsaved changes</span>
          )}
          <button
            onClick={savePriorityOrder}
            disabled={savingPriority || !hasUnsavedChanges}
            className="px-4 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
          >
            {savingPriority ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Order
              </>
            )}
          </button>
        </div>
      </div>

      {/* Interactive Application Order Table */}
      <div className="bg-white rounded-xl border-2 border-slate-900 overflow-hidden">
        <div className="bg-gradient-to-r from-[#26a69a] to-[#1c857e] px-6 py-4">
          <h4 className="text-white font-semibold">Your Application Timeline</h4>
          <p className="text-white/90 text-sm mt-1">
            Drag to reorder, or use the arrows. ED schools are prioritized automatically.
          </p>
        </div>

        {customPriorityOrder.length === 0 ? (
          <div className="p-12 text-center">
            <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-stone-600">Loading your colleges...</p>
          </div>
        ) : (
          <div className="divide-y divide-stone-200">
            {customPriorityOrder.map((college, index) => {
              const isED = college.application_round?.includes('early_decision');
              const deadlineText = getDeadlineText(college);
              const roundDisplay = getRoundDisplay(college.application_round);

              const isExpanded = expandedColleges.has(college.id);

              return (
                <div key={college.id} className={`${isED ? 'bg-amber-50 border-l-4 border-amber-500' : ''}`}>
                  {/* Main college row */}
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`p-4 hover:bg-stone-50 transition-colors cursor-move ${draggedIndex === index ? 'opacity-50' : ''}`}
                  >
                    <div className="grid grid-cols-[40px_50px_1fr_120px_150px_80px_40px] gap-4 items-center">
                      {/* Drag Handle */}
                      <div className="flex items-center justify-center">
                        <GripVertical className="w-5 h-5 text-stone-400" />
                      </div>

                      {/* Order Number */}
                      <div className="flex items-center justify-center">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center font-bold text-sm ${
                          isED ? 'bg-amber-500 text-white' : 'bg-slate-900 text-white'
                        }`}>
                          {index + 1}
                        </div>
                      </div>

                      {/* College Info */}
                      <div>
                        <h5 className="font-semibold text-slate-900 mb-0.5">{college.name}</h5>
                        <div className="flex items-center gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                            college.tier === 'reach' ? 'bg-red-100 text-red-800' :
                            college.tier === 'target' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {college.tier?.charAt(0).toUpperCase() + college.tier?.slice(1)}
                          </span>
                          {isED && (
                            <span className="inline-block px-2 py-0.5 rounded-md text-xs font-bold bg-amber-100 text-amber-900">
                              PRIORITY
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Application Round */}
                      <div className="text-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          isED
                            ? 'bg-amber-100 text-amber-900'
                            : college.application_round === 'early_action'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-stone-100 text-stone-700'
                        }`}>
                          {roundDisplay}
                        </span>
                      </div>

                      {/* Deadline */}
                      <div className="flex items-center gap-2 text-sm text-stone-700">
                        <Calendar className="w-4 h-4 text-stone-400" />
                        <span>{deadlineText}</span>
                      </div>

                      {/* Move Buttons */}
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => moveCollegeUp(index)}
                          disabled={index === 0}
                          className="p-1.5 rounded-md hover:bg-stone-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move up"
                        >
                          <svg className="w-4 h-4 text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => moveCollegeDown(index)}
                          disabled={index === customPriorityOrder.length - 1}
                          className="p-1.5 rounded-md hover:bg-stone-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move down"
                        >
                          <svg className="w-4 h-4 text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>

                      {/* Expand Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCollegeExpansion(college.id);
                        }}
                        className="p-1.5 rounded-md hover:bg-stone-200 transition-colors cursor-pointer"
                        title="View hours breakdown"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-stone-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-stone-600" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Hours Breakdown Expansion */}
                  {isExpanded && (
                    <CollegeHoursBreakdown
                      collegeId={college.id}
                      collegeName={college.name}
                      calculateCollegeHours={calculateCollegeHours}
                      toggleCollegeMVV={toggleCollegeMVV}
                      savingMVV={savingMVV}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* AI-Generated Recommendations (Optional) */}
      {applicationOrder && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6 mt-6">
          <div className="flex items-start gap-2 mb-4">
            <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              AI-Generated Recommendations
            </h4>
          </div>

          {applicationOrder.effortDistribution && (
            <div className="mb-4">
              <h5 className="font-semibold text-slate-900 text-sm mb-2">Recommended Effort Distribution</h5>
              <p className="text-sm text-stone-700 leading-relaxed">{applicationOrder.effortDistribution}</p>
            </div>
          )}

          {applicationOrder.applicationSequence && applicationOrder.applicationSequence.length > 0 && (
            <div>
              <h5 className="font-semibold text-slate-900 text-sm mb-3">AI Strategy Notes</h5>
              <div className="space-y-2">
                {applicationOrder.applicationSequence.map((item, idx) => (
                  <div key={idx} className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-slate-900 mb-1">{item.school || item.name}</p>
                    <p className="text-xs text-stone-600 leading-relaxed">{item.reasoning || item.rationale}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generate AI Recommendations Button */}
      {!applicationOrder && (
        <div className="bg-stone-50 border-2 border-stone-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-[#26a69a] flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 mb-2">Want AI-powered strategy recommendations?</h4>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Get personalized insights on application sequencing, effort distribution, and strategic priorities
                based on your specific portfolio and deadlines.
              </p>
              <button
                onClick={generateApplicationOrder}
                disabled={loadingOrder}
                className="px-4 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
              >
                {loadingOrder ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate AI Recommendations
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderEssayStrategy = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-slate-900">Essay & Narrative Strategy</h3>
        <button
          onClick={createEssayStrategy}
          disabled={loadingEssay}
          className="px-4 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        >
          {loadingEssay ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Create Essay Strategy
            </>
          )}
        </button>
      </div>

      {/* AI Analysis Results */}
      {essayStrategy && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          <div className="flex items-start gap-2 mb-4">
            <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              AI Essay Strategy
            </h4>
          </div>

          {essayStrategy.commonThemes && essayStrategy.commonThemes.length > 0 && (
            <div className="mb-6">
              <h5 className="font-semibold text-base text-slate-900 mb-3">Common Essay Themes</h5>
              <div className="space-y-2">
                {essayStrategy.commonThemes.map((theme, idx) => (
                  <div key={idx} className="bg-white border-l-4 border-[#643eef] pl-4 pr-3 py-3 rounded-r-md shadow-sm">
                    <p className="text-sm text-stone-700 leading-relaxed">{theme}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {essayStrategy.writingOrder && essayStrategy.writingOrder.length > 0 && (
            <>
              <div className="h-px bg-stone-200 my-6"></div>
              <div className="mb-6">
                <h5 className="font-semibold text-base text-slate-900 mb-3">Recommended Writing Order</h5>
                <div className="space-y-4">
                  {essayStrategy.writingOrder.map((item, idx) => (
                    <div key={idx} className="bg-white border border-stone-200 rounded-lg p-4 grid grid-cols-[28px_1fr] gap-3 items-start hover:border-[#26a69a] hover:shadow-sm transition-all">
                      <div className="w-7 h-7 bg-slate-900 text-white rounded-md flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div>
                        <h6 className="font-semibold text-base text-slate-900 mb-1">{item.essay || item}</h6>
                        {item.reasoning && (
                          <p className="text-sm text-stone-700 leading-relaxed">{item.reasoning}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {essayStrategy.narrativeStrategy && (
            <>
              <div className="h-px bg-stone-200 my-6"></div>
              <div className="mb-6">
                <h5 className="font-semibold text-base text-slate-900 mb-2">Narrative Strategy</h5>
                <p className="text-sm text-stone-700 leading-relaxed">{essayStrategy.narrativeStrategy}</p>
              </div>
            </>
          )}

          {essayStrategy.efficiencyTips && essayStrategy.efficiencyTips.length > 0 && (
            <div>
              <h5 className="font-semibold text-slate-900 text-sm mb-3 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                Efficiency Tips
              </h5>
              <ul className="space-y-2">
                {essayStrategy.efficiencyTips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                    <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Default Display */}
      {!essayStrategy && !loadingEssay && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          <h4 className="font-semibold text-slate-900 mb-3 text-sm">Essay Strategy Overview</h4>
          <p className="text-sm text-stone-700 leading-relaxed mb-4">
            Get AI-powered analysis of essay themes, writing order, and narrative strategy across all {colleges.length} colleges.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-semibold text-slate-900 mb-2">Common Themes to Expect:</h5>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 text-sm text-stone-700 pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Why Major / Academic Interest
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Community Contribution
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Leadership Experience
                </li>
                <li className="flex items-start gap-3 text-sm text-stone-700 pl-5 relative">
                  <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                  Diversity & Perspective
                </li>
              </ul>
            </div>
            <div>
              <h5 className="text-sm font-semibold text-slate-900 mb-2">Typical Writing Order:</h5>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="font-bold text-[#26a69a] text-sm">1.</span>
                  <span className="text-sm text-stone-700">Common App Personal Statement</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-bold text-[#26a69a] text-sm">2.</span>
                  <span className="text-sm text-stone-700">"Why Major" essays (reusable)</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-bold text-[#26a69a] text-sm">3.</span>
                  <span className="text-sm text-stone-700">School-specific "Why Us" essays</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-bold text-[#26a69a] text-sm">4.</span>
                  <span className="text-sm text-stone-700">Short answer questions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDecisionFramework = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-slate-900">Decision Framework</h3>
        <button
          onClick={buildDecisionFramework}
          disabled={loadingFramework}
          className="px-4 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white text-sm font-semibold rounded-lg border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
        >
          {loadingFramework ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Building...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Build Decision Framework
            </>
          )}
        </button>
      </div>

      {/* AI Analysis Results */}
      {decisionFramework && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          <div className="flex items-start gap-2 mb-4">
            <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              AI Decision Framework
            </h4>
          </div>

          {decisionFramework.decisionScenarios && decisionFramework.decisionScenarios.length > 0 && (
            <div className="space-y-4 mb-6">
              {decisionFramework.decisionScenarios.map((scenario, idx) => (
                <div key={idx} className="bg-white border border-stone-200 rounded-lg p-4">
                  <h5 className="font-semibold text-sm text-slate-900 mb-2">{scenario.scenario}</h5>
                  <p className="text-sm text-stone-700 leading-relaxed mb-3">{scenario.guidance}</p>
                  {scenario.actionSteps && scenario.actionSteps.length > 0 && (
                    <ul className="space-y-2">
                      {scenario.actionSteps.map((step, stepIdx) => (
                        <li key={stepIdx} className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-md p-2">
                          <CheckCircle className="w-3.5 h-3.5 text-[#26a69a] flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-stone-700 leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {decisionFramework.prioritizationFactors && decisionFramework.prioritizationFactors.length > 0 && (
            <>
              <div className="h-px bg-stone-200 my-6"></div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2 mb-3">
                  <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                    Key Prioritization Factors
                  </h5>
                </div>
                <ul className="space-y-2">
                  {decisionFramework.prioritizationFactors.map((factor, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed pl-5 relative">
                      <div className="absolute left-2 top-2.5 w-1 h-1 bg-[#26a69a] rounded-full"></div>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {decisionFramework.readinessChecklist && decisionFramework.readinessChecklist.length > 0 && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <div className="w-1 h-4 bg-[#26a69a] rounded-full flex-shrink-0 mt-0.5"></div>
                <h5 className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                  Readiness Checklist
                </h5>
              </div>
              <ul className="space-y-2">
                {decisionFramework.readinessChecklist.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 bg-white border border-stone-200 rounded-md p-2">
                    <span className="text-[#26a69a] font-bold flex-shrink-0">✓</span>
                    <span className="text-xs text-stone-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Default Display */}
      {!decisionFramework && !loadingFramework && (
        <div className="bg-white rounded-xl border-2 border-slate-900 p-6">
          <h4 className="font-semibold text-slate-900 mb-4 text-sm">General Decision Framework</h4>

          <div className="space-y-4">
            <div>
              <h5 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                If admitted to multiple targets
              </h5>
              <p className="text-sm text-stone-700 leading-relaxed">
                Compare financial aid packages, specific program strengths, and campus culture fit.
                Visit if possible before May 1 decision deadline.
              </p>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                If waitlisted at top choices
              </h5>
              <p className="text-sm text-stone-700 leading-relaxed">
                Send letter of continued interest, submit fall grades, and deposit at a confirmed school
                while remaining on waitlists.
              </p>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#26a69a] rounded-full"></div>
                Final decision checklist
              </h5>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-md p-2">
                  <span className="text-[#26a69a] font-bold flex-shrink-0 text-sm">✓</span>
                  <span className="text-xs text-stone-700 leading-relaxed">Compare net costs after financial aid</span>
                </li>
                <li className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-md p-2">
                  <span className="text-[#26a69a] font-bold flex-shrink-0 text-sm">✓</span>
                  <span className="text-xs text-stone-700 leading-relaxed">Review specific program offerings in your major</span>
                </li>
                <li className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-md p-2">
                  <span className="text-[#26a69a] font-bold flex-shrink-0 text-sm">✓</span>
                  <span className="text-xs text-stone-700 leading-relaxed">Consider location and campus culture preferences</span>
                </li>
                <li className="flex items-start gap-2 bg-stone-50 border border-stone-200 rounded-md p-2">
                  <span className="text-[#26a69a] font-bold flex-shrink-0 text-sm">✓</span>
                  <span className="text-xs text-stone-700 leading-relaxed">Evaluate career services and outcomes data</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-xs text-stone-600 mt-4">
            Click "Build Decision Framework" for personalized AI guidance tailored to your specific portfolio and circumstances.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone-200">
        <h1 className="text-xl font-semibold text-slate-900">Portfolio Strategy</h1>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 pb-3 border-b border-stone-200 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap border-b-2 ${
                isActive
                  ? 'text-[#26a69a] border-[#26a69a]'
                  : 'text-stone-500 border-transparent hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div ref={tabContentRef} className="bg-[#fafaf9]">
        {activeTab === 'ed-strategy' && renderEDStrategy()}
        {activeTab === 'portfolio-confidence' && renderPortfolioConfidence()}
        {activeTab === 'recommendations' && renderRecommendations()}
        {activeTab === 'essay-strategy' && renderEssayStrategy()}
        {activeTab === 'decision-framework' && renderDecisionFramework()}
      </div>
    </div>
  );
};

export default PortfolioStrategy;